import { useEffect, useState } from "react";
import { apiClient } from "../lib/api";

/**
 * useOsrmRoute — real driving-route geometry for the focused trip's tether.
 *
 * Fixes VA Walkthrough Finding 3 (straight cartesian line cutting across
 * blocks/rivers). Calls the backend OSRM proxy (`/api/floor/routing/route`),
 * which talks to the free public OSRM demo server. Degrades gracefully:
 * on any failure (offline, no route found, rate-limited) `coords` stays
 * null and the caller should fall back to the honest straight-line tether
 * rather than block the map.
 */
const _cache = new Map();

export function useOsrmRoute(pickup, dropoff) {
  const [coords, setCoords] = useState(null);
  const [failed, setFailed] = useState(false);

  const olat = pickup?.lat, olng = pickup?.lng, dlat = dropoff?.lat, dlng = dropoff?.lng;

  useEffect(() => {
    if (olat == null || olng == null || dlat == null || dlng == null) {
      setCoords(null);
      return undefined;
    }
    const key = `${olat.toFixed(4)},${olng.toFixed(4)}-${dlat.toFixed(4)},${dlng.toFixed(4)}`;
    if (_cache.has(key)) {
      setCoords(_cache.get(key));
      setFailed(false);
      return undefined;
    }
    let alive = true;
    setFailed(false);

    const fetchRoute = () => apiClient.get("/floor/routing/route", { params: { olat, olng, dlat, dlng } });

    // The OSRM demo server is a free, shared, no-key third party — occasional
    // timeouts/rate-limits are transient. One retry after a short delay
    // avoids permanently falling back to the honest-but-ugly straight line
    // for what's usually a one-off blip rather than a real outage.
    fetchRoute()
      .catch(() => new Promise((resolve) => setTimeout(() => resolve(fetchRoute()), 1500)))
      .then(({ data }) => {
        if (!alive) return;
        const latlngs = (data?.coordinates || []).map(([lng, lat]) => [lat, lng]);
        if (latlngs.length > 1) {
          _cache.set(key, latlngs);
          setCoords(latlngs);
        } else {
          setFailed(true);
        }
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [olat, olng, dlat, dlng]);

  return { coords, failed };
}
